import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color as ThreeColor,
  Points,
  PointsMaterial,
  type Scene,
} from 'three';

interface Particle {
  vx: number;
  vy: number;
  vz: number;
  life: number;
  maxLife: number;
}

export interface ConfettiSystem {
  step(dt: number): void;
  destroy(): void;
}

export const launchConfetti = (
  scene: Scene,
  origin: { x: number; y: number },
  palette: readonly string[],
  count = 140,
): ConfettiSystem => {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const particles: Particle[] = [];
  const cTmp = new ThreeColor();

  for (let i = 0; i < count; i++) {
    positions[i * 3] = origin.x;
    positions[i * 3 + 1] = origin.y;
    positions[i * 3 + 2] = 1.5;
    cTmp.set(palette[i % palette.length] ?? '#ffffff');
    colors[i * 3] = cTmp.r;
    colors[i * 3 + 1] = cTmp.g;
    colors[i * 3 + 2] = cTmp.b;
    const angle = Math.random() * Math.PI * 2;
    const speed = 6 + Math.random() * 8;
    particles.push({
      vx: Math.cos(angle) * speed,
      vy: 5 + Math.random() * 7,
      vz: 2 + Math.random() * 5,
      life: 0,
      maxLife: 2.0 + Math.random() * 1.4,
    });
  }

  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(positions, 3));
  geo.setAttribute('color', new BufferAttribute(colors, 3));
  const mat = new PointsMaterial({
    size: 0.32,
    vertexColors: true,
    transparent: true,
    opacity: 1,
    blending: AdditiveBlending,
    depthWrite: false,
  });
  const points = new Points(geo, mat);
  scene.add(points);

  return {
    step(dt) {
      const arr = positions;
      let alive = 0;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]!;
        p.life += dt;
        if (p.life > p.maxLife) continue;
        alive++;
        p.vy -= 9.8 * dt;
        arr[i * 3]! += p.vx * dt;
        arr[i * 3 + 1]! += p.vy * dt;
        arr[i * 3 + 2]! += p.vz * dt;
      }
      geo.attributes['position']!.needsUpdate = true;
      mat.opacity = Math.max(0, 1 - particles[0]!.life / particles[0]!.maxLife);
      if (alive === 0) this.destroy();
    },
    destroy() {
      scene.remove(points);
      geo.dispose();
      mat.dispose();
    },
  };
};
